import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { navigationPath } from '../../App';
import { createUploadHandler } from './utils/file-upload';
import UrlField from './components/url-field';
import FloatingSaveButton from './components/floating-save-button';
import BannerUploadField from './components/banner-upload-field';
import { useHoverPreview } from './components/hover-preview';

interface ContentPair {
  id: string;
  title: string;
  url: string;
  contentNumber: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}

interface ApkLiteData {
  id: string;
  sliderImage1: string;
  sliderImage2: string;
  sliderImage3: string;
  sliderImage4: string;
  sliderImage5: string;
  popupImage: string;
  contentPairs: ContentPair[];
}

const ContentCard = ({
  title,
  children,
  className = '',
  headerAction,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  headerAction?: React.ReactNode;
}) => {
  return (
    <div className={`bg-[#1c243b] rounded-lg shadow-lg overflow-hidden ${className}`}>
      <div className='bg-[#212a40] px-4 py-3 border-b border-[#2d3649] flex items-center justify-between'>
        <h3 className='text-md font-medium text-white'>{title}</h3>
        {headerAction}
      </div>
      <div className='p-5 space-y-4'>{children}</div>
    </div>
  );
};

const ApkLite = () => {
  const navigate = useNavigate();
  const { handleMouseMove, handleMouseLeave, HoverPreviewComponent } = useHoverPreview();

  const [apkLiteData, setApkLiteData] = useState<ApkLiteData>({
    id: '',
    sliderImage1: '',
    sliderImage2: '',
    sliderImage3: '',
    sliderImage4: '',
    sliderImage5: '',
    popupImage: '',
    contentPairs: [],
  });
  const [uploading, setUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContentPair, setNewContentPair] = useState<{ title: string; link: string }>({
    title: '',
    link: '',
  });

  const uploadHandler = createUploadHandler(setUploading, {
    navigate,
    navigateTo: navigationPath.dashboard,
    successMessage: 'Berhasil Upload',
    errorMessage: 'Terjadi kesalahan ketika upload.',
  });

  const handleBannerUpload = (file: File, endpoint: string) => {
    return uploadHandler(file, { endpoint });
  };

  const handleSliderImage1Upload = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-slider-1');
  };

  const handleSliderImage2Upload = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-slider-2');
  };

  const handleSliderImage3Upload = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-slider-3');
  };

  const handleSliderImage4Upload = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-slider-4');
  };

  const handleSliderImage5Upload = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-slider-5');
  };

  const handlePopupImageUpload = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-popup');
  };

  const handleChange = (field: keyof ApkLiteData, value: string) => {
    setApkLiteData(prev => ({ ...prev, [field]: value }));
  };

  const handleContentPairChange = (id: string, field: 'title' | 'url', value: string) => {
    setApkLiteData(prev => ({
      ...prev,
      contentPairs: prev.contentPairs.map(pair => (pair.id === id ? { ...pair, [field]: value } : pair)),
    }));
  };

  const addContentPair = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/cms/create-apk-lite', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newContentPair.title,
          url: newContentPair.link,
        }),
      });

      const res = await response.json();
      if (!response.ok) {
        throw new Error(res.errors?.[0]?.message || res.message || 'Failed to create content pair');
      }

      const fetchResponse = await fetch(`/api/cms/get-data-apk-lite`);
      const fetchRes = await fetchResponse.json();
      if (fetchRes.data_content && fetchRes.data_banner) {
        const contentPairs: ContentPair[] = fetchRes.data_content.map((item: any) => ({
          id: item.id,
          title: item.title,
          url: item.url,
          contentNumber: item.contentNumber,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          version: item.version,
        }));

        const banner = fetchRes.data_banner;

        const data: ApkLiteData = {
          id: banner.id || '',
          sliderImage1: banner.slider1 || '',
          sliderImage2: banner.slider2 || '',
          sliderImage3: banner.slider3 || '',
          sliderImage4: banner.slider4 || '',
          sliderImage5: banner.slider5 || '',
          popupImage: banner.popup || '',
          contentPairs: contentPairs,
        };

        setApkLiteData(data);
      }

      setNewContentPair({ title: '', link: '' });
      setShowAddModal(false);
      toast.success('Content pair created successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create content pair');
    } finally {
      setIsLoading(false);
    }
  };

  const updateContentPair = async (contentNumber: number, title: string, url: string) => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/cms/update-apk-lite', {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contentNumber,
          title,
          url,
        }),
      });

      const res = await response.json();
      if (!response.ok) {
        throw new Error(res.errors?.[0]?.message || res.message || 'Failed to update content pair');
      }

      const fetchResponse = await fetch(`/api/cms/get-data-apk-lite`);
      const fetchRes = await fetchResponse.json();
      if (fetchRes.data_content && fetchRes.data_banner) {
        const contentPairs: ContentPair[] = fetchRes.data_content.map((item: any) => ({
          id: item.id,
          title: item.title,
          url: item.url,
          contentNumber: item.contentNumber,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          version: item.version,
        }));

        const banner = fetchRes.data_banner;

        const data: ApkLiteData = {
          id: banner.id || '',
          sliderImage1: banner.slider1 || '',
          sliderImage2: banner.slider2 || '',
          sliderImage3: banner.slider3 || '',
          sliderImage4: banner.slider4 || '',
          sliderImage5: banner.slider5 || '',
          popupImage: banner.popup || '',
          contentPairs: contentPairs,
        };

        setApkLiteData(data);
      }

      toast.success('Content pair updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update content pair');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteContentPair = async (contentNumber: number) => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/cms/delete-apk-lite', {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contentNumber,
        }),
      });

      const res = await response.json();
      if (!response.ok) {
        throw new Error(res.errors?.[0]?.message || res.message || 'Failed to delete content pair');
      }

      const fetchResponse = await fetch(`/api/cms/get-data-apk-lite`);
      const fetchRes = await fetchResponse.json();
      if (fetchRes.data_content && fetchRes.data_banner) {
        const contentPairs: ContentPair[] = fetchRes.data_content.map((item: any) => ({
          id: item.id,
          title: item.title,
          url: item.url,
          contentNumber: item.contentNumber,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          version: item.version,
        }));

        const banner = fetchRes.data_banner;

        const data: ApkLiteData = {
          id: banner.id || '',
          sliderImage1: banner.slider1 || '',
          sliderImage2: banner.slider2 || '',
          sliderImage3: banner.slider3 || '',
          sliderImage4: banner.slider4 || '',
          sliderImage5: banner.slider5 || '',
          popupImage: banner.popup || '',
          contentPairs: contentPairs,
        };

        setApkLiteData(data);
      }

      toast.success('Content pair deleted successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete content pair');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchApkLiteData = async () => {
      try {
        const response = await fetch(`/api/cms/get-data-apk-lite`);
        const res = await response.json();
        if (res.data_content && res.data_banner) {
          const contentPairs: ContentPair[] = res.data_content.map((item: any) => ({
            id: item.id,
            title: item.title,
            url: item.url,
            contentNumber: item.contentNumber,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            version: item.version,
          }));

          const banner = res.data_banner;

          const data: ApkLiteData = {
            id: banner.id || '',
            sliderImage1: banner.slider1 || '',
            sliderImage2: banner.slider2 || '',
            sliderImage3: banner.slider3 || '',
            sliderImage4: banner.slider4 || '',
            sliderImage5: banner.slider5 || '',
            popupImage: banner.popup || '',
            contentPairs: contentPairs,
          };

          setApkLiteData(data);
        }
      } catch (error) {
        toast.error('Gagal mengambil data APK Lite');
      }
    };

    fetchApkLiteData();
  }, [uploading]);

  const save = async () => {
    try {
      setIsLoading(true);

      toast.success('All changes have been saved');
    } catch (error: any) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      <ContentCard title='Image Management'>
        <div className='space-y-4'>
          <BannerUploadField
            label='Slider Image 1'
            value={apkLiteData.sliderImage1}
            onChange={value => handleChange('sliderImage1', value)}
            onUpload={handleSliderImage1Upload}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Slider Image 2'
            value={apkLiteData.sliderImage2}
            onChange={value => handleChange('sliderImage2', value)}
            onUpload={handleSliderImage2Upload}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Slider Image 3'
            value={apkLiteData.sliderImage3}
            onChange={value => handleChange('sliderImage3', value)}
            onUpload={handleSliderImage3Upload}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Slider Image 4'
            value={apkLiteData.sliderImage4}
            onChange={value => handleChange('sliderImage4', value)}
            onUpload={handleSliderImage4Upload}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Slider Image 5'
            value={apkLiteData.sliderImage5}
            onChange={value => handleChange('sliderImage5', value)}
            onUpload={handleSliderImage5Upload}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Popup Image'
            value={apkLiteData.popupImage}
            onChange={value => handleChange('popupImage', value)}
            onUpload={handlePopupImageUpload}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </div>
      </ContentCard>

      <ContentCard
        title='Content Management'
        headerAction={
          <button
            onClick={() => setShowAddModal(true)}
            className='bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors'
            title='Add New Content Pair'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6v6m0 0v6m0-6h6m-6 0H6' />
            </svg>
          </button>
        }
      >
        <div className='space-y-4'>
          <div className='space-y-2'>
            {apkLiteData.contentPairs.map((pair, index) => (
              <div
                key={pair.id}
                className='bg-[#212a40] rounded-lg p-3 border border-[#2d3649] hover:border-[#374151] transition-colors'
              >
                <div className='flex items-center gap-3'>
                  <div className='flex-1 min-w-0'>
                    <input
                      type='text'
                      value={pair.title}
                      onChange={e => handleContentPairChange(pair.id, 'title', e.target.value)}
                      className='w-full h-8 bg-[#1c243b] text-white border border-gray-600 rounded px-2 text-sm focus:outline-none focus:border-blue-500'
                      placeholder='Enter title'
                    />
                  </div>

                  <div className='flex-1 min-w-0'>
                    <input
                      type='text'
                      value={pair.url}
                      onChange={e => handleContentPairChange(pair.id, 'url', e.target.value)}
                      className='w-full h-8 bg-[#1c243b] text-white border border-gray-600 rounded px-2 text-sm focus:outline-none focus:border-blue-500'
                      placeholder='Enter link URL'
                    />
                  </div>

                  <div className='flex items-center gap-1 flex-shrink-0'>
                    {/* Update Button */}
                    <button
                      onClick={() => updateContentPair(pair.contentNumber, pair.title, pair.url)}
                      className='hover:bg-green-700 text-white p-1.5 rounded transition-colors'
                      title='Update content pair'
                      disabled={isLoading}
                    >
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                        />
                      </svg>
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => deleteContentPair(pair.contentNumber)}
                      className='text-red-400 hover:text-red-300 p-1.5 hover:bg-red-900/20 rounded transition-colors'
                      title='Delete content pair'
                      disabled={isLoading}
                    >
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {apkLiteData.contentPairs.length === 0 && (
              <div className='text-center text-gray-400 py-8'>
                <p>No content pairs added yet.</p>
                <p className='text-sm'>Click the "+" button to get started.</p>
              </div>
            )}
          </div>
        </div>
      </ContentCard>

      {showAddModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-[#1c243b] rounded-lg p-6 w-full max-w-md mx-4 border border-[#2d3649]'>
            <div className='flex items-center justify-between mb-4'>
              <h3 className='text-lg font-medium text-white'>Add New Content Pair</h3>
              <button onClick={() => setShowAddModal(false)} className='text-gray-400 hover:text-white transition-colors'>
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>

            <div className='space-y-4'>
              <UrlField
                label='Title'
                value={newContentPair.title}
                onChange={value => setNewContentPair(prev => ({ ...prev, title: value }))}
                placeholder='Enter title'
              />
              <UrlField
                label='Link'
                value={newContentPair.link}
                onChange={value => setNewContentPair(prev => ({ ...prev, link: value }))}
                placeholder='Enter link URL'
              />
            </div>

            <div className='flex gap-3 mt-6'>
              <button
                onClick={() => setShowAddModal(false)}
                className='flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={addContentPair}
                disabled={!newContentPair.title.trim() || !newContentPair.link.trim()}
                className='flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors'
              >
                Add Content Pair
              </button>
            </div>
          </div>
        </div>
      )}

      {HoverPreviewComponent}
      <FloatingSaveButton onClick={save} isLoading={isLoading} />
    </div>
  );
};

export default ApkLite;
